import { useState } from 'react';
import { withStyles } from '@mui/styles';
import { useTheme } from '@mui/material/styles';
import { Box, Typography, Stepper, Step, StepLabel } from '@mui/material';

import Card from '../../../../common/card';
import Divider from '../../../../common/divider/divider';
import WithQriosBackground from '../../../../common/qrios-background/withQriosBackground';

import { getInitStep } from './utils';
import { STEPS_LIST } from './consts';
import { OnFailure, GoToNextStep, ActiveStepState } from './interfaces';
import { creatingProfileContentHandler } from './creatingProfileContentHandler';

const StyledStepLabel = withStyles(theme => ({
  label: {
    fontSize: '12px !important'
  },
  completed: {
    color: `#00683E !important`
  },
  error: {
    color: `${theme.palette.warning.main} !important`
  },
  iconContainer: {
    '& > svg.Mui-error': {
      color: `${theme.palette.warning.main} !important`
    }
  }
}))(StepLabel);

const ProfileCreation = () => {
  const [activeStepState, setActiveStepState] = useState<ActiveStepState>('active');
  const [activeStepNumber, setActiveStepNumber] = useState<number>(getInitStep());
  const [msisdn, setMsisdn] = useState<string>();
  const [shortcode, setShortcode] = useState<string>();
  const theme = useTheme();

  const hasStepError = (stepNumber: number): boolean =>
    stepNumber === activeStepNumber && (activeStepState === 'error' || activeStepState === 'warning');

  const goToNextStep: GoToNextStep = (newMsisdn?: string, newShortcode?: string) => {
    setActiveStepState('active');
    setActiveStepNumber(prevActiveStepNumber => prevActiveStepNumber + 1);
    if (newMsisdn) {
      setMsisdn(newMsisdn);
    }
    if (newShortcode) {
      setShortcode(newShortcode);
    }
  };

  const onFailure: OnFailure = failedState => {
    setActiveStepState(failedState);
  };

  const content = (
    <Box p={3} data-testid="creating-a-new-client-profile-dialog">
      <Box mb={2}>
        <Typography align="left" variant="h2">
          Creating a new client profile
        </Typography>
      </Box>
      <Stepper activeStep={activeStepNumber}>
        {STEPS_LIST.map((label, index) => {
          const stepProps: { completed?: boolean } = {};
          const labelProps: { error?: boolean } = {};

          if (hasStepError(index)) {
            labelProps.error = true;
          }

          return (
            <Step key={label} {...stepProps}>
              <StyledStepLabel {...labelProps}>{label}</StyledStepLabel>
            </Step>
          );
        })}
      </Stepper>
      <Divider />
      {creatingProfileContentHandler({ activeStepNumber, goToNextStep, onFailure, msisdn, shortcode })}
    </Box>
  );

  return (
    <WithQriosBackground title="Get started with a free Qrios account">
      <Box
        sx={{
          width: theme.customComponents.profileCreation.container.width,
          maxWidth: theme.customComponents.profileCreation.container.maxWidth,
          margin: 'auto'
        }}
      >
        <Card content={content} />
      </Box>
    </WithQriosBackground>
  );
};

export default ProfileCreation;
